import {assert} from 'assert-ts';
import {Concept} from 'types';
import type {CodeListMap} from 'schemaDefinition/types';
import type {ExpressionV4, Metadata, MetadataOrder} from '../types';
import {getManifestationCount} from './getManifestationCount';
import {orderExpressionsByProps} from './orderExpressionsByProps';
import {sortManifestations} from './sortManifestations';

/**
 * Orderes expressions with groups of ordered manifestations with any change requests:
 * - Expressions by language and format
 * - Expression group:
 *   - Manifestations with any corresponding change requests ordered descending
 *     by published year
 *     - Change requests, if any (in given order)
 *     - Manifestation
 */
export const getMetadataOrder = (
  data: Metadata,
  newExpressionIds: string[],
  codelists: CodeListMap,
): MetadataOrder => {
  // @todo What does this do. Updates readonly data state/crashes in redux toolkit.
  //ensureAllExpressionIdsSet(data);

  const {expressions, manifestations, changeRequests} = data;

  // Order all manifestations by publishedYear
  const manifestationsOrdered = sortManifestations(manifestations);

  // Order expressions by existing expressions first then new expressions
  const existingExpressions = (expressions ?? []).filter(
    e => !newExpressionIds.includes(e.id),
  );
  const newExpressions = (expressions ?? []).filter(e =>
    newExpressionIds.includes(e.id),
  );
  // - existing expressions by props
  // - new expressions by order of creation
  const expressionsOrdered = [
    ...orderExpressionsByProps(existingExpressions, codelists),
    ...newExpressions,
  ]
    // Filter out empty expressions
    .filter(e => getManifestationCount(e.id, data) > 0);

  const order: MetadataOrder = expressionsOrdered.reduce<MetadataOrder>(
    (acc, e) => {
      const manifestationsForExpression = manifestationsOrdered.filter(
        m => m.expressionId === e.id,
      );
      const changeRequestsLevelManifestation = changeRequests?.filter(
        cr => cr.level === 'manifestation',
      );
      const manifestationsWithChangeRequests = manifestationsForExpression
        .map(m => {
          const changeRequestsForManifestation =
            changeRequestsLevelManifestation
              ?.filter(cr => cr.manifestation.id === m.id)
              .map(cr => ({
                type: Concept.changeRequest as const,
                taskId: cr.id,
              }));

          return [
            {
              type: Concept.manifestation as const,
              id: m.id,
              changeRequests: changeRequestsForManifestation,
            },
          ];
        })
        .flat(1);

      acc.push({
        expressionId: e.id,
        type: newExpressionIds.includes(e.id) ? 'new' : 'existing',
        content: manifestationsWithChangeRequests,
      });
      return acc;
    },
    [],
  );

  return order;
};
/**
 * @todo What does this do. Updates readonly data state/crashes in redux toolkit.
 *
 * Update: I think this was used when moving manifestation to another work.
 * In those cases the expressionId on changeRequests was set to the old expressionId because we dont know the new expression id untill backend has placed the manifestation in the new work.
 * BUT, we dont really need the expressionId to be set, we just match on manifestationId directly instead.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ensureAllExpressionIdsSet = (data: Metadata) => {
  if (!data.changeRequests) {
    return;
  }

  data.changeRequests.forEach(cr => {
    if (!cr.expression || cr.expression.id === 'expressionIdPlaceholder') {
      const expressionId = data.manifestations.find(
        m => m.id === cr.manifestation.id,
      )?.expressionId;
      if (
        assert.soft(
          expressionId,
          'ensureAllExpressionIdsSet: expressionid expected',
        )
      ) {
        cr.manifestation.expressionId = expressionId;
        if (!cr.expression) {
          cr.expression = {
            id: expressionId,
            workId: data.work.id,
          } as ExpressionV4;
        } else {
          cr.expression.id = expressionId;
          cr.expression.workId = data.work.id;
        }
      }
    }
  });

  data.changeRequests = data.changeRequests.filter(
    cr => cr.expression.id !== 'expressionIdPlaceholder',
  );
};
